import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { blockedDatesService } from '../../../services/blockedDatesService';

const CalendarBlockingModal = ({ listing, onClose, onUpdate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [blockType, setBlockType] = useState('single'); // 'single', 'range', 'template'
  const [templateType, setTemplateType] = useState('weekend');
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [existingBlocks, setExistingBlocks] = useState([]);
  const [activeTab, setActiveTab] = useState('block'); // 'block' or 'manage'

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    loadExistingBlocks();
  }, [listing?.id]);

  const loadExistingBlocks = async () => {
    try {
      const blocks = await blockedDatesService?.fetchBlockedDates(listing?.id);
      setExistingBlocks(blocks || []);
    } catch (error) {
      console.error('Failed to load blocked dates:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();
    return { daysInMonth, startingDayOfWeek };
  };

  const isDateBlocked = (date) => {
    const dateString = date?.toISOString()?.split('T')?.[0];
    return existingBlocks?.some(block => {
      if (block?.blockType === 'single') {
        return block?.startDate === dateString;
      } else {
        const start = new Date(block?.startDate);
        const end = block?.endDate ? new Date(block?.endDate) : start;
        return date >= start && date <= end;
      }
    });
  };

  const handleDateClick = (date) => {
    const dateString = date?.toISOString()?.split('T')?.[0];

    if (blockType === 'range') {
      if (!rangeStart || (rangeStart && rangeEnd)) {
        setRangeStart(date);
        setRangeEnd(null);
        setSelectedDates([dateString]);
      } else {
        if (date < rangeStart) {
          setRangeStart(date);
          setRangeEnd(null);
          setSelectedDates([dateString]);
        } else {
          setRangeEnd(date);
          const dates = [];
          let currentDate = new Date(rangeStart);
          while (currentDate <= date) {
            dates?.push(currentDate?.toISOString()?.split('T')?.[0]);
            currentDate?.setDate(currentDate?.getDate() + 1);
          }
          setSelectedDates(dates);
        }
      }
    } else {
      // Single date selection
      if (selectedDates?.includes(dateString)) {
        setSelectedDates(selectedDates?.filter(d => d !== dateString));
      } else {
        setSelectedDates([...selectedDates, dateString]);
      }
    }
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth?.getFullYear(), currentMonth?.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth?.getFullYear(), currentMonth?.getMonth() + 1));
  };

  const handleSelectAllWeekends = () => {
    const { daysInMonth } = getDaysInMonth(currentMonth);
    const weekendDates = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth?.getFullYear(), currentMonth?.getMonth(), day);
      const dayOfWeek = date?.getDay();
      if ((dayOfWeek === 0 || dayOfWeek === 6) && date >= new Date(new Date()?.setHours(0, 0, 0, 0))) {
        weekendDates?.push(date?.toISOString()?.split('T')?.[0]);
      }
    }
    setSelectedDates([...new Set([...selectedDates, ...weekendDates])]);
  };

  const handleSelectAllMonth = () => {
    const { daysInMonth } = getDaysInMonth(currentMonth);
    const allDates = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth?.getFullYear(), currentMonth?.getMonth(), day);
      if (date >= new Date(new Date()?.setHours(0, 0, 0, 0))) {
        allDates?.push(date?.toISOString()?.split('T')?.[0]);
      }
    }
    setSelectedDates([...new Set([...selectedDates, ...allDates])]);
  };

  const handleClearSelection = () => {
    setSelectedDates([]);
    setRangeStart(null);
    setRangeEnd(null);
  };

  const handleApplyBlocking = async () => {
    if (selectedDates?.length === 0) {
      alert('Please select at least one date to block');
      return;
    }

    setIsLoading(true);
    try {
      if (blockType === 'range' && rangeStart && rangeEnd) {
        await blockedDatesService?.blockDateRange(
          listing?.id,
          listing?.ownerId,
          rangeStart?.toISOString()?.split('T')?.[0],
          rangeEnd?.toISOString()?.split('T')?.[0],
          reason
        );
      } else if (blockType === 'template') {
        const startDate = selectedDates?.[0];
        const endDate = selectedDates?.[selectedDates?.length - 1];
        await blockedDatesService?.applyTemplate(
          listing?.id,
          listing?.ownerId,
          templateType,
          startDate,
          endDate,
          `${templateType?.charAt(0)?.toUpperCase() + templateType?.slice(1)} Template`,
          reason
        );
      } else {
        await blockedDatesService?.blockMultipleDates(
          listing?.id,
          listing?.ownerId,
          selectedDates,
          reason
        );
      }

      await loadExistingBlocks();
      handleClearSelection();
      setReason('');
      if (onUpdate) onUpdate();
      alert('Dates blocked successfully!');
    } catch (error) {
      console.error('Failed to block dates:', error);
      alert('Failed to block dates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!confirm('Are you sure you want to remove this blocked date?')) return;

    try {
      await blockedDatesService?.deleteBlockedDate(blockId);
      await loadExistingBlocks();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to delete blocked date:', error);
      alert('Failed to remove blocked date. Please try again.');
    }
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const days = [];

  for (let i = 0; i < startingDayOfWeek; i++) {
    days?.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    days?.push(new Date(currentMonth?.getFullYear(), currentMonth?.getMonth(), day));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-elevation-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-foreground mb-1">
              Calendar Blocking
            </h2>
            <p className="font-body text-sm text-muted-foreground">
              {listing?.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-muted transition-smooth"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} strokeWidth={2} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border px-6">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('block')}
              className={`px-4 py-3 font-medium text-sm border-b-2 transition-smooth ${
                activeTab === 'block' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Block Dates
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`px-4 py-3 font-medium text-sm border-b-2 transition-smooth ${
                activeTab === 'manage' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Manage Blocks ({existingBlocks?.length})
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'block' ? (
            <>
              {/* Block Type Selection */}
              <div className="mb-6">
                <label className="font-body text-sm font-medium text-foreground mb-2 block">
                  Block Type
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => {
                      setBlockType('single');
                      handleClearSelection();
                    }}
                    className={`p-3 rounded-lg border-2 transition-smooth ${
                      blockType === 'single' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name="Calendar" size={20} className="mx-auto mb-1" />
                    <p className="font-body text-sm font-medium">Single Dates</p>
                  </button>
                  <button
                    onClick={() => {
                      setBlockType('range');
                      handleClearSelection();
                    }}
                    className={`p-3 rounded-lg border-2 transition-smooth ${
                      blockType === 'range' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name="CalendarRange" size={20} className="mx-auto mb-1" />
                    <p className="font-body text-sm font-medium">Date Range</p>
                  </button>
                  <button
                    onClick={() => {
                      setBlockType('template');
                      handleClearSelection();
                    }}
                    className={`p-3 rounded-lg border-2 transition-smooth ${
                      blockType === 'template' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name="Sparkles" size={20} className="mx-auto mb-1" />
                    <p className="font-body text-sm font-medium">Template</p>
                  </button>
                </div>
              </div>

              {/* Template Type Selection */}
              {blockType === 'template' && (
                <div className="mb-6">
                  <label className="font-body text-sm font-medium text-foreground mb-2 block">
                    Template Type
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => setTemplateType('weekend')}
                      className={`p-3 rounded-lg border transition-smooth ${
                        templateType === 'weekend' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <p className="font-body text-sm font-medium">Weekends</p>
                    </button>
                    <button
                      onClick={() => setTemplateType('holiday')}
                      className={`p-3 rounded-lg border transition-smooth ${
                        templateType === 'holiday' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <p className="font-body text-sm font-medium">Holidays</p>
                    </button>
                    <button
                      onClick={() => setTemplateType('seasonal')}
                      className={`p-3 rounded-lg border transition-smooth ${
                        templateType === 'seasonal' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <p className="font-body text-sm font-medium">Seasonal</p>
                    </button>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSelectAllWeekends}
                    className="px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-smooth"
                  >
                    Select Weekends
                  </button>
                  <button
                    onClick={handleSelectAllMonth}
                    className="px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-smooth"
                  >
                    Select All Month
                  </button>
                  <button
                    onClick={handleClearSelection}
                    className="px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted rounded-md transition-smooth"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Calendar */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={handlePreviousMonth}
                    className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-muted transition-smooth"
                    aria-label="Previous month"
                  >
                    <Icon name="ChevronLeft" size={20} strokeWidth={2} />
                  </button>

                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    {monthNames?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
                  </h3>

                  <button
                    onClick={handleNextMonth}
                    className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-muted transition-smooth"
                    aria-label="Next month"
                  >
                    <Icon name="ChevronRight" size={20} strokeWidth={2} />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-2 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
                    <div key={day} className="text-center font-caption text-xs font-medium text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-2">
                  {days?.map((date, index) => {
                    if (!date) {
                      return <div key={index} className="aspect-square" />;
                    }

                    const dateString = date?.toISOString()?.split('T')?.[0];
                    const isSelected = selectedDates?.includes(dateString);
                    const isBlocked = isDateBlocked(date);
                    const isPast = date < new Date(new Date()?.setHours(0, 0, 0, 0));

                    return (
                      <button
                        key={index}
                        onClick={() => !isPast && !isBlocked && handleDateClick(date)}
                        disabled={isPast || isBlocked}
                        className={`aspect-square flex items-center justify-center rounded-md font-body text-sm transition-smooth ${
                          isPast
                            ? 'text-muted-foreground/30 cursor-not-allowed'
                            : isBlocked
                            ? 'bg-error/20 text-error cursor-not-allowed'
                            : isSelected
                            ? 'bg-primary text-primary-foreground font-semibold'
                            : 'text-foreground hover:bg-muted border border-border'
                        }`}
                      >
                        {date?.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-4 text-sm mb-6">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-primary rounded" />
                  <span className="font-body text-muted-foreground">Selected</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-error/20 rounded" />
                  <span className="font-body text-muted-foreground">Already Blocked</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border border-border rounded" />
                  <span className="font-body text-muted-foreground">Available</span>
                </div>
              </div>

              {/* Reason Input */}
              <div className="mb-6">
                <label className="font-body text-sm font-medium text-foreground mb-2 block">
                  Reason (Optional)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e?.target?.value)}
                  placeholder="e.g., Maintenance, Personal use, etc."
                  className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Selection Summary */}
              {selectedDates?.length > 0 && (
                <div className="p-4 bg-muted rounded-md mb-6">
                  <p className="font-body text-sm text-foreground">
                    <span className="font-semibold">Selected:</span> {selectedDates?.length} day{selectedDates?.length !== 1 ? 's' : ''}
                    {blockType === 'range' && rangeStart && rangeEnd && (
                      <span className="ml-2">({rangeStart?.toLocaleDateString()} - {rangeEnd?.toLocaleDateString()})</span>
                    )}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleApplyBlocking}
                  loading={isLoading}
                  disabled={selectedDates?.length === 0}
                  className="flex-1"
                >
                  Block {selectedDates?.length} Date{selectedDates?.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </>
          ) : (
            /* Manage Blocks Tab */
            <div className="space-y-3">
              {existingBlocks?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <p className="font-body text-muted-foreground">No blocked dates yet</p>
                </div>
              ) : (
                existingBlocks?.map((block) => (
                  <div key={block?.id} className="p-4 bg-muted rounded-lg flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon
                          name={block?.blockType === 'single' ? 'Calendar' : 'CalendarRange'}
                          size={16}
                          className="text-primary"
                        />
                        <p className="font-body text-sm font-semibold text-foreground">
                          {block?.blockType === 'single'
                            ? new Date(block?.startDate)?.toLocaleDateString()
                            : `${new Date(block?.startDate)?.toLocaleDateString()} - ${new Date(block?.endDate)?.toLocaleDateString()}`}
                        </p>
                      </div>
                      {block?.reason && (
                        <p className="font-body text-xs text-muted-foreground">{block?.reason}</p>
                      )}
                      {block?.templateName && (
                        <p className="font-body text-xs text-primary mt-1">
                          Template: {block?.templateName}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteBlock(block?.id)}
                      className="ml-4 p-2 text-error hover:bg-error/10 rounded-md transition-smooth"
                      aria-label="Delete block"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarBlockingModal;